<script setup>
  import { useBreakpoints } from '@storefront/storefront';
  import { computed } from 'vue';

  const breakpoints = useBreakpoints();

  const props = defineProps({
    title: { type: String, required: false },
    div: { type: Boolean, required: false },
    link: { type: String, required: false },
    buttonText: { type: String, required: false },
    headerType: { type: String, required: false },
  });

  const component = computed(() => {
    if (props.div) {
      return 'div';
    } else {
      return 'section';
    }
  });

  const safeHeaderType = computed(() => {
    return props.headerType === 'h1' ? 'h2' : props.headerType;
  });
</script>
<template>
  <component :is="component" class="section">
    <header v-if="title || link" class="section__header">
      <SfHeading v-if="title" :type="safeHeaderType">
        {{ title }}
      </SfHeading>
      <SfButton v-if="link" transparent :href="link" :small="!breakpoints.md" class="section__button">
        <template #default>
          {{ buttonText || $t('SHOP.COMMON.ACTION.GO_TO') }}
        </template>
        <template #after>
          <SfIconChevronRight aria-hidden="true" />
        </template>
      </SfButton>
    </header>
    <div class="section__content" v-if="$slots.default()">
      <slot />
    </div>
  </component>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --section-gap: 1.5rem;
    --section-padding: 2rem;
    --section-background: #fff;
    --section-border: none;
    --section-border-inline: var(--section-border);
    --section-border-block: var(--section-border);
    --section-border-radius: calc(var(--border-radius) * 1.5);
    --section-content-gap: 1.5rem;

    @include media.query(md) {
      --section-gap: 0.75rem;
      --section-padding: 1rem 0;
      --section-content-gap: 1rem;
      --section-border-radius: 0;
    }
  }
</style>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .section {
    display: flex;
    flex-direction: column;
    gap: var(--section-gap);
    width: 100%;
    padding: var(--section-padding);
    background: var(--section-background);
    border-inline: var(--section-border-inline);
    border-block: var(--section-border-block);
    border-radius: var(--section-border-radius);
    overflow: hidden;

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    &__button {
      margin-left: auto;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--section-content-gap);

      &:deep(.ql-editor) {
        white-space: unset;
      }
    }
  }

  @include media.query(lg) {
    .section {
      &__header {
        padding: 0 0.625rem;
      }

      &__content > :deep(.ql-editor) {
        padding: 0 0.625rem;
      }
    }
  }
</style>
